import { createContext, useContext, useState } from 'react';

import { FCWithChildren } from 'common/types';

import type {
  PaymentCardOrderFlowContext,
  PaymentCardOrderFlowSharedState,
} from '../../types';

// Context

export interface VirtualOrderFlowConfig {
  cardType: 'basicDebitCard' | 'businessWorldDebitCard';
  bankAccount: {
    bankAccountId: string;
    nextBalance: number;
  };
  freeCardsIncluded: number;
  freeCardsLeft: number;
  formattedPlanName: string;
  priceWithoutVAT: number;
  priceAfterQuotaReachedWithoutVAT: number;
}

export interface VirtualOrderFlowSharedState
  extends PaymentCardOrderFlowSharedState {
  deactivateAt: string | null;
}

type VirtualCardOrderFlowContext = PaymentCardOrderFlowContext<
  VirtualOrderFlowConfig,
  VirtualOrderFlowSharedState
>;

const VIRTUAL_CARD_ORDER_FLOW_CONTEXT = createContext<
  VirtualCardOrderFlowContext | undefined
>(undefined);

// Provider
interface ProviderProps {
  value: VirtualOrderFlowConfig;
}

const INITIAL_SHARED_STATE: VirtualOrderFlowSharedState = {
  alias: undefined,
  deactivateAt: null,
};

export const VirtualCardOrderFlowContextProvider: FCWithChildren<
  ProviderProps
> = (props) => {
  const { children, value: flowConfig } = props;

  const [sharedState, setSharedState] = useState(INITIAL_SHARED_STATE);

  return (
    <VIRTUAL_CARD_ORDER_FLOW_CONTEXT.Provider
      value={{
        flowConfig,
        setSharedState,
        sharedState,
      }}
    >
      {children}
    </VIRTUAL_CARD_ORDER_FLOW_CONTEXT.Provider>
  );
};

// Hook
export function useVirtualCardOrderFlowContext() {
  const context = useContext(VIRTUAL_CARD_ORDER_FLOW_CONTEXT);
  if (!context) {
    throw new Error(
      '"useVirtualCardOrderFlowContext" can only be used under VirtualCardOrderFlowContextProvider',
    );
  }
  return context;
}
